/**
 * types/hedgeFundViewModel.ts — MER Algo Footprints v7.1H
 *
 * View-model for Hedge Fund Mode UI.
 * Pure display contract — no engine logic, no scoring.
 * Produced exclusively by mapper functions from engine outputs.
 */

export type HFBias           = 'bullish' | 'bearish' | 'neutral';
export type HFState          = 'ready' | 'watch' | 'avoid';
export type HFTimeSensitivity= 'immediate' | 'developing' | 'expiring' | 'none';
export type HFIntegrity      = 'strong' | 'mixed' | 'weak' | 'unknown';
export type HFTrapStatus     = 'confirmed' | 'developing' | 'failed' | 'none';
export type HFGammaInfluence = 'low' | 'medium' | 'high' | 'unknown';
export type HFGammaPosture   = 'supportive' | 'neutral' | 'opposing' | 'unavailable';

export interface HedgeFundModeViewModel {
  symbol:    string;
  timestamp: string;

  decision: {
    bias:            HFBias;
    setupName:       string | null;
    state:           HFState;
    readinessScore:  number | null;
    confidence:      number | null;
    timeSensitivity: HFTimeSensitivity;
  };

  structure: {
    summary:         string;
    choch:           string | null;
    bos:             string | null;
    premiumDiscount: string | null;
    integrity:       HFIntegrity;
  };

  liquidity: {
    summary:      string;
    nearestLevel: string | null;
    sweepStatus:  string | null;
    likelyTarget: string | null;
  };

  trap: {
    summary: string;
    active:  boolean;
    type:    string | null;
    status:  HFTrapStatus;
  };

  gamma: {
    summary:   string;
    influence: HFGammaInfluence;
    posture:   HFGammaPosture;
    note:      string;
  };

  regime: {
    summary:    string;
    label:      string | null;
    confidence: number | null;
    tradeStyle: string | null;
  };

  execution: {
    entry:        string | null;
    invalidation: string | null;
    target1:      string | null;
    target2:      string | null;
    rr:           string | null;
    checklist:    string[];
  };

  thesis: {
    summary:          string;
    whatChanged:      string;
    confirmation:     string;
    invalidationRule: string;
  };

  review: {
    snapshotId:           string | null;
    outcomeStatus:        string | null;
    aiReviewAvailable:    boolean;
    journalDraftAvailable:boolean;
  };

  // v7.1H: State Transition Layer
  stateTransition: {
    state:            string;
    actionability:    string;
    directionalBias:  string;
    setupQuality:     number;
    confidence:       number;
    // P3 confidence penalties: the governed operational band for `confidence`.
    effectiveConfidenceBand?: 'high' | 'medium' | 'low' | null;
    rationale:        string[];
    invalidation:     string[];
    longTrigger:      string | null;
    shortTrigger:     string | null;
    confirmation:     string[];
    failure:          string[];
    contextFlags:     string[];
    operatorGuidance: string;
    stateColor:       string;
    actionLabel:      string;
  };
}

/** Minimal fallback for when context is unavailable */
export function emptyViewModel(symbol: string): HedgeFundModeViewModel {
  return {
    symbol,
    timestamp: new Date().toISOString(),
    decision:  { bias: 'neutral', setupName: null, state: 'avoid', readinessScore: null, confidence: null, timeSensitivity: 'none' },
    structure: { summary: 'Insufficient data', choch: null, bos: null, premiumDiscount: null, integrity: 'unknown' },
    liquidity: { summary: 'No liquidity data', nearestLevel: null, sweepStatus: null, likelyTarget: null },
    trap:      { summary: 'No trap data', active: false, type: null, status: 'none' },
    gamma:     { summary: 'Gamma unavailable', influence: 'unknown', posture: 'unavailable', note: 'No options data' },
    regime:    { summary: 'No regime data', label: null, confidence: null, tradeStyle: null },
    execution: { entry: null, invalidation: null, target1: null, target2: null, rr: null, checklist: [] },
    thesis:    { summary: 'No thesis available', whatChanged: '—', confirmation: '—', invalidationRule: '—' },
    review:    { snapshotId: null, outcomeStatus: null, aiReviewAvailable: false, journalDraftAvailable: false },
    stateTransition: {
      state: 'NO_VALID_SETUP', actionability: 'avoid', directionalBias: 'neutral',
      setupQuality: 0, confidence: 0, rationale: [], invalidation: [],
      longTrigger: null, shortTrigger: null, confirmation: [], failure: [],
      contextFlags: [], operatorGuidance: 'No data — context not available',
      stateColor: '#3a4558', actionLabel: 'STAND ASIDE',
    },
  };
}
